import * as React from 'react'
import moment from 'moment'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../stores/CurrentUserStore'
import { inject, observer } from 'mobx-react'
import { InventoryLogType, IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { PermissionNames } from '@getgreenline/homi-shared'
import { Table, Popover, Icon, Tooltip, Button, Alert } from 'antd'
import { Routes } from '../../constants/Routes'
import { capitalizeFirstLetter } from '../../utilities/helpers'
import { ModifyLogModal } from '../../containers/Dashboard/DashboardReports/Compliance/FixInventoryLogs/ModifyLogModal'
import { ProcessOnDatePicker } from './ProcessOnDatePicker'
import { ModifyLogModalOCS } from '../../containers/Dashboard/DashboardCompliance/InventoryCorrections/ModifyLogModalOCS'
import { IProcessOnDateData } from '../../containers/Dashboard/DashboardCompliance/ProcessOnDateLogs/ProcessOnDateLogs'
import { ProcessOnDateData } from './ProcessOnDateData'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  companyId: number
  logs: IInventoryLog[] | undefined
  showProductColumn?: boolean
  onChange: () => void
  isLoading: boolean
  processOnDateData?: IProcessOnDateData[]
}

interface State {
  selectedModifyLog?: IInventoryLog
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class InventoryLogsTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {}
  }

  render() {
    const { companyId, logs, showProductColumn } = this.props
    const responsiveEmployeeColumnWidth = showProductColumn ? { width: 120 } : { width: 92 }
    const responsiveNumberColumnWidth = { width: 70 }

    const entity = this.props.currentCompanyStore
    const user = this.props.currentUserStore

    const userCanViewOCSCompliance = entity && user ? user.canViewOCSCompliance(entity) : false
    const processOnDateData = this.props.processOnDateData

    const columns = [
      {
        title: 'Date',
        key: 'date',
        width: 92,
        render: (log: IInventoryLog) => (
          <span>
            <div>{moment(log.createDate).format('MMM D YYYY')}</div>
            <div>{moment(log.createDate).format('hh:mm a')}</div>
          </span>
        ),
      },
      {
        title: 'Type',
        key: 'type',
        width: 92,
        render: (log: IInventoryLog) => {
          const breakdown = logBreakdown(log)
          const canModify = breakdown.originalType === 'audit' || !breakdown.originalHref
          const hasPermission = this.props.currentUserStore!.hasPermissionAtEntity(
            PermissionNames.CAN_UPDATE_INVENTORY_LOGS,
            log.entityId,
          )

          return (
            <span>
              <Popover
                trigger='click'
                placement='right'
                title={
                  <div className='d-flex'>
                    <span style={{ flexGrow: 1 }}>Inventory log</span>
                    <span className='text-muted'>ID: {log.id}</span>
                  </div>
                }
                content={
                  <div style={{ width: 300 }}>
                    <div className='mb-2'>
                      {!breakdown.updateType ? (
                        // Log has not updated
                        breakdown.originalHref ? (
                          // Link exists
                          <a target='_blank' href={breakdown.originalHref} rel='noreferrer'>
                            <Button type='primary' style={{ width: '100%' }}>
                              Go to {breakdown.originalType}
                            </Button>
                          </a>
                        ) : (
                          // Link does not exist
                          <Alert
                            type='warning'
                            message={
                              <>
                                <span>Added from "Inventory - Overview" page</span>
                              </>
                            }
                          />
                        )
                      ) : (
                        // Log has been updated
                        <Alert
                          type='warning'
                          message={
                            <>
                              <div>This log was modified:</div>
                              <div className='text-muted'>{log.updateUserName}</div>
                              <p className='text-muted'>
                                {moment(log.updateDate!).format('MMM D YYYY h:mm a')}
                              </p>
                              <div>Original type:</div>
                              {breakdown.originalHref ? (
                                <span className='text-muted'>
                                  <a target='_blank' href={breakdown.originalHref} rel='noreferrer'>
                                    {capitalizeFirstLetter(breakdown.originalType!)}
                                  </a>
                                </span>
                              ) : (
                                <span className='text-muted'>
                                  Added from "Inventory - Overview" page
                                </span>
                              )}
                            </>
                          }
                        />
                      )}
                    </div>
                    {
                      // Only allow audits and former audits to be modified
                      canModify && (
                        <div className='mb-2'>
                          <Button
                            style={{ width: '100%' }}
                            disabled={!hasPermission}
                            onClick={() => this.setState({ selectedModifyLog: log })}
                          >
                            Modify log type
                          </Button>
                        </div>
                      )
                    }
                  </div>
                }
              >
                <a>{log.type}</a>
                {!!log.updateDate && (
                  <Tooltip
                    title={
                      <>
                        <p>This log has been modified</p>
                        <div>{log.updateUserName}</div>
                        <div>{moment(log.updateDate).format('MMM D YYYY h:mm a')}</div>
                      </>
                    }
                  >
                    <Icon type='info-circle' style={{ marginLeft: 5 }} />
                  </Tooltip>
                )}
              </Popover>
            </span>
          )
        },
      },
      ...(showProductColumn
        ? [
            {
              title: 'SKU',
              key: 'productSku',
              render: (log: IInventoryLog) => log.productSku,
              width: 100,
            },
            {
              title: 'Barcode',
              key: 'productBarcode',
              render: (log: IInventoryLog) => log.productBarcode,
              width: 150,
              className: 'truncate-column',
              ellipsis: true,
            },
            {
              title: 'Product',
              key: 'product',
              render: (log: IInventoryLog) => (
                <a
                  href={Routes.EDIT_PRODUCT(companyId, log.parentProductId || log.productId)}
                  target='_blank'
                  rel='noreferrer'
                >
                  {log.productName}
                </a>
              ),
              width: 200,
            },
          ]
        : []),
      {
        title: 'Employee',
        key: 'employee',
        render: (log: IInventoryLog) => log.userName,
        ...responsiveEmployeeColumnWidth,
      },
      {
        title: 'Location',
        key: 'location',
        width: 110,
        render: (log: IInventoryLog) => log.entityName,
      },
      {
        title: 'Opening',
        key: 'start',
        render: (log: IInventoryLog) => log.previousValue,
        align: 'right' as const,
        ...responsiveNumberColumnWidth,
      },
      {
        title: 'Change',
        key: 'change',
        render: (log: IInventoryLog) => (
          <b
            className={
              log.updateValue > 0
                ? 'text-success'
                : log.updateValue < 0
                ? 'text-danger'
                : 'text-muted'
            }
          >
            {log.updateValue > 0 ? '+' : ''}
            {log.updateValue}
          </b>
        ),
        align: 'right' as const,
        ...responsiveNumberColumnWidth,
      },
      {
        title: 'Closing',
        key: 'end',
        render: (log: IInventoryLog) => log.newValue,
        align: 'right' as const,
        ...responsiveNumberColumnWidth,
      },
      ...(userCanViewOCSCompliance
        ? [
            {
              title: processOnDateData ? (
                <span>
                  <span className='mr-1'>Process on date</span>
                  <Tooltip title={<div>Sorted from latest to earliest.</div>}>
                    <Icon type='question-circle' />
                  </Tooltip>
                </span>
              ) : (
                <span>
                  <span className='mr-1'>Process on date</span>
                  <Tooltip
                    title={
                      <div>
                        Use this field when the date of the transaction differs from the date
                        displayed on the dashboard.
                      </div>
                    }
                  >
                    <Icon type='question-circle' />
                  </Tooltip>
                </span>
              ),
              key: 'processOnDate',
              width: 140,
              render: (text: any, log: IInventoryLog, index: number) =>
                processOnDateData && logs ? (
                  // Note: logs and processOnDateData have been sorted in a similar way in the ProcessOnDateLogs component, so the log's index can be used to access the corresponding data in the ProcessOnDateData array.
                  <ProcessOnDateData logProcessOnDate={processOnDateData[index].processOnDate} />
                ) : (
                  <ProcessOnDatePicker log={log} />
                ),
            },
          ]
        : []),
      {
        title: '',
        key: 'notes',
        render: (log: IInventoryLog) =>
          log.note && (
            <Popover
              content={
                <div className={'gr-sales-notes'}>
                  <p>{log.note}</p>
                </div>
              }
              placement='left'
              title='Notes'
            >
              <Icon type='info-circle' />
            </Popover>
          ),
        width: 36,
      },
    ]

    return (
      <>
        <Table
          className='mb-2 background-white show-horizontal-scrollbar'
          pagination={false}
          size='middle'
          dataSource={logs}
          columns={columns}
          rowKey={(logs) => logs.id.toString()}
          scroll={{ x: '100%' }}
          loading={this.props.isLoading}
        />
        {this.state.selectedModifyLog &&
          (userCanViewOCSCompliance ? (
            <ModifyLogModalOCS
              onHide={() => this.setState({ selectedModifyLog: undefined })}
              onUpdate={() => {
                this.props.onChange()
                this.setState({ selectedModifyLog: undefined })
              }}
              log={this.state.selectedModifyLog}
            />
          ) : (
            <ModifyLogModal
              onHide={() => this.setState({ selectedModifyLog: undefined })}
              onUpdate={() => {
                this.props.onChange()
                this.setState({ selectedModifyLog: undefined })
              }}
              log={this.state.selectedModifyLog}
            />
          ))}
      </>
    )
  }
}

const logBreakdown = (log: IInventoryLog) => {
  let breakdown: {
    originalType: InventoryLogType | null
    originalHref: string | null
    updateType: InventoryLogType | null
  }

  const updateType = log.updateUserId ? log.type : null

  if (log.transferId) {
    breakdown = {
      originalType: InventoryLogType.TRANSFER,
      originalHref: Routes.EDIT_INVENTORY_BULK_TRANSFER(log.companyId, log.transferId),
      updateType: updateType,
    }
  } else if (log.auditId) {
    breakdown = {
      originalType: InventoryLogType.AUDIT,
      originalHref: Routes.EDIT_INVENTORY_AUDIT(log.companyId, log.auditId),
      updateType: updateType,
    }
  } else if (log.invoiceId) {
    breakdown = {
      originalType: InventoryLogType.PURCHASE_ORDER,
      originalHref: Routes.EDIT_INVOICE(log.companyId, log.invoiceId),
      updateType: updateType,
    }
  } else if (log.paymentId) {
    breakdown = {
      originalType: log.type,
      originalHref: Routes.DASHBOARD_SALES(log.companyId, log.paymentId),
      updateType: updateType,
    }
  } else if (log.conversionId) {
    breakdown = {
      originalType: InventoryLogType.CONVERT,
      originalHref: Routes.DASHBOARD_INVENTORY_CONVERT(log.companyId),
      updateType: updateType,
    }
  } else {
    // One-off audit, no IDs assigned
    breakdown = {
      originalType: null,
      originalHref: null,
      updateType: updateType,
    }
  }

  return breakdown
}
